import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
	Component,
	Input,
	OnInit,
	inject,
	output,
	input,
	InputSignal,
	OutputEmitterRef,
	DestroyRef
} from '@angular/core';
import {
	DataMigrationTypesEnum,
	Languages,
	ltrDayMonthFormat,
	MappedNotification,
	migrationExportTypes,
	NavBarAction,
	Notification,
	NotificationIconType,
	NotificationOpenModalAction,
	NotificationRedirectType,
	NotificationTypeEnum,
	NotificationTypesModel,
	rtlDayMonthFormat,
	RunMigrationUrl,
	SNACK_BAR_CANCELLATION_DURATION,
	UserRole
} from '@core';
import { NotificationActionsService } from '@app/services/notification-actions/notification-actions.service';
import { filter, tap } from 'rxjs/operators';
import { SnackBarIcon, SnackBarParams, SnackBarService, SnackBarType } from '@ui/lib/shared-services';
import { TranslateService } from '@ngx-translate/core';
import { MigrationsDataService } from '@app/services/migrations/migrations-data.service';
import { format, getHours, getMinutes, isToday, isYesterday } from 'date-fns';
import { Direction } from '@angular/cdk/bidi';
import { NotificationSettingsService } from '@app/services/notification-settings/notification-settings.service';
import { checkUserPermissionForFinanceAndSecretary, getNameForLedesFile } from '@app/services/helpers/helpers.helper';
import { Params, Router } from '@angular/router';
import { PassDataService } from '@app/core/services/pass-data/pass-data.service';
import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { Observable } from 'rxjs';
import { UserService } from '@app/core/services/user/user.service';
import { SettingsService } from '@app/core/services/settings/settings.service';
import { Firm } from '@app/core/services/settings/settings.model';
import { MainRoutes } from '@app/shared/enums/general.enums';
import { FirmRegion } from '@app/core/services/settings/settings.enum';
import { LocalStorageItems } from '@app/core/services/localstorage/localstorage.enum';

@Component({
	selector: 'lawyal-nav-bar',
	templateUrl: './nav-bar.component.html',
	styleUrls: ['./nav-bar.component.scss'],
	standalone: false
})
export class NavBarComponent implements OnInit {
	private readonly destroyRef: DestroyRef = inject(DestroyRef);

	private notificationActionService = inject(NotificationActionsService);
	private snackService = inject(SnackBarService);
	private migrationsDataService = inject(MigrationsDataService);
	private notificationSettingsService = inject(NotificationSettingsService);
	private router = inject(Router);
	private passDataService = inject(PassDataService);
	private settingsService = inject(SettingsService);
	private newUserService = inject(UserService);
	private breakpointObserver = inject(BreakpointObserver);
	translateService = inject(TranslateService);

	readonly actionSelected: OutputEmitterRef<NavBarAction> = output<NavBarAction>();
	readonly languageChanged: OutputEmitterRef<void> = output<void>();

	readonly documentsManagement: InputSignal<boolean> = input<boolean>(undefined);
	readonly newMatter: InputSignal<boolean> = input<boolean>(undefined);
	readonly tasks: InputSignal<boolean> = input<boolean>(false);

	@Input() set notifications(value: Notification[]) {
		// TODO: Move to a separated component.
		if (value && Array.isArray(value)) {
			this.activeNotifications = this.mapNotificationData(value);
			this.calcNotifications();
		}
	}

	@Input() set notificationAdded(value: Notification) {
		// TODO: Move to a separated component.
		if (value && this.isPhoneBreakPoint) {
			const mappedNotification: MappedNotification = this.mapNotification(value);
			this.activeNotifications.unshift(mappedNotification);
			if (!value.read) {
				this.notificationsCounter++;
				this.assignHasNewNotifications();
				// TODO: add animation.
				// this.rotateNotificationButton();
			}

			if (value.message?.toast) {
				const routerUrl: string = this.router.url;
				if (
					routerUrl.includes('/document') &&
					routerUrl.includes('proforma-invoice/') &&
					value.message?.proforma_invoice
				) {
					const updatedInvoice: Params = value.message.proforma_invoice;
					this.passDataService.changeData(updatedInvoice);
				}
				if (mappedNotification.actualType === NotificationTypeEnum.MattersError) {
					return;
				}
				if (mappedNotification.actualType === NotificationTypeEnum.ClientsMattersError) {
					return;
				}
				this.openSnackBar(mappedNotification);
			}
		}
	}

	@Input() set notificationDeleted(value: Notification) {
		// TODO: Move to a separated component.
		if (value) {
			const index = this.activeNotifications.findIndex(item => item.id === value.id);
			if (index > -1) {
				if (!this.activeNotifications[index].read) {
					this.notificationsCounter--;
					this.assignHasNewNotifications();
				}

				this.activeNotifications.splice(index, 1);
			}
		}
	}

	private phoneBreakPoint$: Observable<BreakpointState>;
	private isPhoneBreakPoint: boolean;

	activeNotifications: MappedNotification[] = []; // TODO: Move to a separated component.
	notificationsCounter: number; // TODO: Move to a separated component.
	hasNewNotifications: boolean; // TODO: Move to a separated component.
	currentLang: string; // TODO: Move to a separated component. use enum.
	displayFormat: string; // TODO: Move to a separated component.
	direction: Direction; // TODO: Move to a separated component.

	public readonly firmRegionEnum: typeof FirmRegion = FirmRegion;
	public readonly navBarOption: typeof NavBarAction = NavBarAction;
	public readonly userRole: typeof UserRole = UserRole;
	public readonly mainRoutes: typeof MainRoutes = MainRoutes;

	public alternativeLang: Languages;
	public ilTaxEnabled: boolean;
	public hasLawyalTime: boolean;
	public isFinanceOrSecretary: boolean;
	public displaySetVATOption: boolean;
	public displayWhatsAppSignup: boolean;
	public firmRegion: FirmRegion;

	constructor() {
		this.phoneBreakPoint$ = this.breakpointObserver.observe(Breakpoints.XSmall);
		this.phoneBreakPoint$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result: BreakpointState) => {
			this.isPhoneBreakPoint = result.matches;
		});
	}

	ngOnInit(): void {
		this.currentLang = this.translateService.currentLang;
		this.alternativeLang = this.translateService.currentLang === Languages.HE ? Languages.EN : Languages.HE;
		this.displayFormat = this.currentLang === Languages.HE ? rtlDayMonthFormat : ltrDayMonthFormat;
		this.settingsService.firm$
			.pipe(
				filter((firm: Firm) => !!firm),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe((firm: Firm): void => {
				this.ilTaxEnabled = firm.il_tax;
				this.hasLawyalTime = firm.lawyal_time;
				this.displaySetVATOption = firm.firm_region === FirmRegion.Israel && this.newUserService.isAdmin();
				this.displayWhatsAppSignup = firm.whatsapp === true && this.newUserService.isAdmin();
				this.isFinanceOrSecretary = checkUserPermissionForFinanceAndSecretary(this.newUserService.getActiveUser());
				this.firmRegion = firm.firm_region;
			});
	}

	public emitSelectedAction(action: NavBarAction): void {
		this.actionSelected.emit(action);
	}

	public decrementCounter(): void {
		// TODO: Move to a separated component.
		this.notificationsCounter--;
		this.assignHasNewNotifications();
	}

	public openModalFromSnackBar(notification: Notification) {
		// TODO: Move to a separated component.
		let notificationType: NotificationTypesModel = this.notificationActionService.getActualNotificationType(
			notification.type
		);
		switch (notificationType?.type) {
			case NotificationTypeEnum.MigrationCheckFail:
				const notificationAction: NotificationOpenModalAction = {
					openMigrationModal: true,
					notification: notification
				};
				this.notificationActionService.toggleMigrationModal = notificationAction;
		}
	}

	public calcNotifications(): void {
		// TODO: Move to a separated component.
		this.notificationsCounter = 0;
		this.activeNotifications.forEach(item => {
			if (!item.read) {
				this.notificationsCounter++;
			}
		});

		if (this.notificationsCounter > 0) {
			//   this.rotateNotificationButton();
			// TODO: add animation.
		}
		this.assignHasNewNotifications();
	}

	public onRunMigration(notification: Notification) {
		// TODO: Move to a separated component.
		this.runMigrationFile(notification);
	}

	private runMigrationFile(notification: Notification): void {
		// TODO: Move to a separated component.
		const path: string = RunMigrationUrl[DataMigrationTypesEnum[notification.message.data_migration?.type]];
		this.migrationsDataService
			.runMigrationFile(notification.message.data_migration?.id, path)
			.pipe(
				tap(() => {
					const snackText: string = this.translateService.instant('NOTIFICATIONS.MIGRATION_FILE_IN_PROCESS');
					const snackBarParams: SnackBarParams = {
						text: snackText
					};

					this.snackService.openSnackBar(SnackBarType.GenerateFile, snackBarParams, SNACK_BAR_CANCELLATION_DURATION);
				})
			)
			.subscribe();
	}

	private assignHasNewNotifications(): void {
		// TODO: Move to a separated component.
		this.hasNewNotifications = this.notificationsCounter > 0;
	}

	private mapNotificationData(data: Notification[]): MappedNotification[] {
		// TODO: Move to a separated component.
		return data.map(item => this.mapNotification(item));
	}

	private mapNotification(notification: Notification): MappedNotification {
		// TODO: Move to a separated component.
		const canMigrated: boolean = migrationExportTypes.find(
			item => item.id === notification.message.data_migration?.type
		)?.enable_upload;

		return {
			...notification,
			time: this.getTime(notification.created_at),
			iconName: this.getIcon(notification),
			title: this.getNotificationTitle(notification),
			actualType: this.notificationActionService.getActualNotificationType(notification.type)?.type,
			canMigrated
		} as MappedNotification;
	}

	private openSnackBar(notification: Notification) {
		// TODO: Move to a separated component.
		let snackText: string;
		let redirectTo: string;

		snackText = this.getSnackBarTitle(notification, notification.canMigrated);
		if (notification.message.redirect) {
			redirectTo = this.buildRedirectPath(notification);
		}
		let snackTextLink: string;
		if (notification.type !== NotificationTypeEnum.Migration) {
			snackTextLink = this.translateService.instant('NOTIFICATIONS.HERE');
		}
		this.direction = this.translateService.currentLang === 'he' ? 'rtl' : 'ltr';
		const snackBarParams: SnackBarParams = {
			text: snackText,
			iconName: this.getSnackBarIcon(notification),
			textLink: snackTextLink,
			link: notification.message.link,
			links: notification.message.links || [],
			redirectPath: redirectTo || '',
			showModal: notification.message.modal,
			fileName: this.getNameForFile(notification),
			notificationActualType: notification.actualType,
			dataMigrationType: notification.message.data_migration?.type,
			canMigrated: notification.canMigrated
		};
		const snackRef = this.snackService.openSnackBar(
			SnackBarType.GenerateFile,
			snackBarParams,
			SNACK_BAR_CANCELLATION_DURATION
		);
		snackRef
			.onAction()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				if (!notification.read) {
					notification.read = true;
					this.decrementCounter();
					this.notificationSettingsService.markNotificationAsRead(notification.id);
				}
				if (notification.message.modal) {
					this.openModalFromSnackBar(notification);
				}

				if (notification.canMigrated) {
					this.runMigrationFile(notification);
				}
			});
	}

	private getTime(date: string): string {
		// TODO: Move to a separated component.
		const activeTime = `${getHours(date) < 10 ? `0${getHours(date)}` : `${getHours(date)}`}:${
			getMinutes(date) < 10 ? `0${getMinutes(date)}` : `${getMinutes(date)}`
		}`;

		if (isToday(date)) {
			return `${this.translateService.instant('NOTIFICATIONS.DROP_DOWN_MENU.TODAY')}, ${activeTime}`;
		} else if (isYesterday(date)) {
			return `${this.translateService.instant('NOTIFICATIONS.DROP_DOWN_MENU.YESTERDAY')}, ${activeTime}`;
		} else {
			return `${format(date, this.displayFormat)}, ${activeTime}`;
		}
	}

	private getNotificationTitle(notification: Notification): string {
		// TODO: Move to a separated component.
		const activeNotification: NotificationTypesModel = this.notificationActionService.getActualNotificationType(
			notification.type
		);
		if (activeNotification) {
			return this.currentLang === Languages.HE ? activeNotification.alt_name : activeNotification.name;
		}
		return '';
	}

	private getIcon(notification: Notification): string {
		// TODO: Move to a separated component.
		const activeNotification: NotificationTypesModel = this.notificationActionService.getActualNotificationType(
			notification.type
		);
		if (activeNotification && activeNotification.icon) {
			return NotificationIconType[activeNotification.icon];
		}
		return '';
	}

	private getSnackBarTitle(notification: Notification, canMigrated: boolean): string {
		// TODO: Move to a separated component.
		let snackText: string;
		if (notification.message.links?.length) {
			snackText = this.translateService.instant('NOTIFICATIONS.DOCUMENTS_READY_TOAST');
		} else if (canMigrated) {
			if (notification.actualType === NotificationTypeEnum.MigrationCheckFail) {
				snackText = this.translateService.instant('NOTIFICATIONS.MIGRATION_FILE_WITH_ERRORS');
			} else {
				if (notification.actualType !== NotificationTypeEnum.Migration) {
					snackText = this.translateService.instant('NOTIFICATIONS.CAN_MIGRATED');
				} else {
					snackText = this.translateService.instant('NOTIFICATIONS.MIGRATION_SUCCESS');
				}
			}
		} else if (notification?.actualType === NotificationTypeEnum.MigrationCheck) {
			snackText = this.translateService.instant('NOTIFICATIONS.MIGRATION_CHECK_SUCCESS');
		} else if (notification.message.modal) {
			snackText = this.translateService.instant('NOTIFICATIONS.MIGRATION_FILE_WITH_ERRORS');
		} else if (notification.message.redirect === NotificationRedirectType.ProformaInvoicePreview) {
			snackText = this.translateService.instant('NOTIFICATIONS.EMAIL_ERROR');
		} else if (notification.message.proforma_invoice || notification?.actualType === NotificationTypeEnum.Email) {
			snackText = this.translateService.instant('NOTIFICATIONS.EMAIL_SUCCESS');
		} else if (notification.message.conflict_check) {
			snackText = this.translateService.instant('NOTIFICATIONS.CONFLICT_CHECK_READY');
		} else if (notification.message.matter && notification.message.redirect === NotificationRedirectType.NewMatter) {
			const interpolateParams: Params = {
				name: notification.message.matter.name,
				iid: notification.message.matter.internal_id
			};
			snackText = this.translateService.instant('NOTIFICATIONS.MATTER_CREATED_SUCCESSFULLY', interpolateParams);
		} else if (notification.message.client && notification.message.redirect === NotificationRedirectType.NewClient) {
			const interpolateParams: Params = {
				name: notification.message.client.name,
				iid: notification.message.client.internal_id
			};
			snackText = this.translateService.instant('NOTIFICATIONS.CLIENT_CREATED_SUCCESSFULLY', interpolateParams);
		} else {
			snackText = this.translateService.instant('NOTIFICATIONS.DOCUMENT_READY_TOAST');
		}

		return snackText;
	}

	private buildRedirectPath(notification: Notification): string {
		// TODO: Move to a separated component.
		let path: string = '';
		switch (notification.message.redirect) {
			case NotificationRedirectType.ProformaInvoicePreview:
				if (notification.message.id) {
					path = `finance-management/proforma-invoice/${notification.message.id}/document`; // TODO: use enum.
				}
		}
		return path;
	}

	private getSnackBarIcon(notification: Notification): string {
		// TODO: Move to a separated component.
		let iconName: string = '';
		let notificationType: NotificationTypesModel = this.notificationActionService.getActualNotificationType(
			notification.type
		);
		switch (notificationType?.type) {
			case NotificationTypeEnum.Pdf:
			case NotificationTypeEnum.Excel:
			case NotificationTypeEnum.Bkmv:
			case NotificationTypeEnum.Ini:
				iconName = SnackBarIcon.CloudDownload;
				break;
			default:
				iconName = SnackBarIcon.Info;
		}
		return iconName;
	}

	private getNameForFile(notification: Notification): string {
		// TODO: Move to a separated component.
		let fileName: string = '';
		let notificationType: NotificationTypesModel = this.notificationActionService.getActualNotificationType(
			notification.type
		);
		switch (notificationType?.type) {
			case NotificationTypeEnum.Ledes:
				fileName = getNameForLedesFile(notification.message.link);
				break;
		}
		return fileName;
	}

	public toggleLanguage(language: Languages): void {
		// this.languageChanged.emit(); // TODO: use in the future when the "location.reload()" will be removed.
		localStorage.setItem(LocalStorageItems.Language, language);
		location.reload(); // TODO: check with the product team if needed.
	}
}
